import type {
  ColeccionDeEstructura,
  ColeccionPublicable,
  ContenidoPendiente,
  DefinicionPendiente,
  DisenoPendiente,
  Grupo,
  PaginaPendiente,
  PedidoDePublicacion,
  Pendiente,
  ProductoPendiente,
  PublicacionDeLaWeb,
  ResultadoDeLote,
  ServicioPendiente,
} from '../api/publicacion'

/**
 * Las decisiones de pantalla de «Publicar mi web», sin React, para poder probarlas con una
 * tabla: qué sale marcado, qué se avisa, en qué tandas se manda y cómo se cuenta lo que
 * pasó. Qué hay pendiente y qué se puede hacer con el diseño lo dice el núcleo; aquí no se
 * decide nada que el núcleo no vuelva a mirar.
 */

/**
 * La estructura de la web (plan del CMS dinámico): va arriba y se manda antes que el contenido
 * —sin el tipo publicado, sus contenidos no tienen dirección—.
 */
export const ESTRUCTURA: readonly ColeccionDeEstructura[] = ['tipos-de-contenido', 'taxonomias', 'grupos-de-campos']

/** En el orden en que se enseñan y se mandan: la estructura, el contenido de siempre y los contenidos de los tipos. */
export const COLECCIONES: readonly ColeccionPublicable[] = [...ESTRUCTURA, 'paginas', 'productos', 'servicios', 'entradas', 'contenidos']

export const esDeEstructura = (c: ColeccionPublicable): c is ColeccionDeEstructura => (ESTRUCTURA as readonly string[]).includes(c)

/** El nombre de cada lista en el cuerpo de `POST /panel/web/publicar` (los de la estructura, sin guiones). */
const EN_EL_PEDIDO = {
  'tipos-de-contenido': 'tiposDeContenido',
  taxonomias: 'taxonomias',
  'grupos-de-campos': 'gruposDeCampos',
  paginas: 'paginas',
  productos: 'productos',
  servicios: 'servicios',
  entradas: 'entradas',
  contenidos: 'contenidos',
} as const satisfies Record<ColeccionPublicable, keyof PedidoDePublicacion>

/** Cuántos documentos van en cada llamada: el tope del núcleo (`TOPE_POR_LLAMADA`), que cuenta todas las listas. */
export const TOPE_POR_TANDA = 60

export const NOMBRES: Record<ColeccionPublicable, { singular: string; plural: string; titulo: string }> = {
  paginas: { singular: 'página', plural: 'páginas', titulo: 'Páginas' },
  productos: { singular: 'producto', plural: 'productos', titulo: 'Productos' },
  servicios: { singular: 'servicio', plural: 'servicios', titulo: 'Servicios' },
  entradas: { singular: 'entrada', plural: 'entradas', titulo: 'Entradas' },
  contenidos: { singular: 'contenido', plural: 'contenidos', titulo: 'Contenidos' },
  'tipos-de-contenido': { singular: 'tipo de contenido', plural: 'tipos de contenido', titulo: 'Tipos de contenido' },
  taxonomias: { singular: 'taxonomía', plural: 'taxonomías', titulo: 'Taxonomías' },
  'grupos-de-campos': { singular: 'grupo de campos', plural: 'grupos de campos', titulo: 'Grupos de campos' },
}

export type Seleccion = Record<ColeccionPublicable, ReadonlySet<number>> & { diseno: boolean }

type Elemento = { id: number }

/** Lo pendiente de una colección, donde lo pone el núcleo (la estructura va junta en `estructura`); `null` si la web no la tiene. */
export const grupoDe = (p: Pendiente, coleccion: ColeccionPublicable): Grupo<Elemento & { cambios?: boolean }> | null => {
  if (coleccion === 'tipos-de-contenido') return p.estructura?.tiposDeContenido ?? null
  if (coleccion === 'taxonomias') return p.estructura?.taxonomias ?? null
  if (coleccion === 'grupos-de-campos') return p.estructura?.gruposDeCampos ?? null
  return p[coleccion] ?? null
}

export const elementosDe = (p: Pendiente, coleccion: ColeccionPublicable): Elemento[] => grupoDe(p, coleccion)?.borradores ?? []

/**
 * Si el diseño se puede marcar: hay una versión lista y quien mira puede publicarla o
 * solicitarla. Con `version` (Diseño, que publica la que se está mirando en la previa), esa,
 * si no es ya la publicada.
 */
export const disenoDisponible = (d: DisenoPendiente, version?: null | number): boolean =>
  d.puede !== 'nada' && (version ? version !== d.publicada : d.pendiente && d.situacion === 'lista')

/** Una selección sin nada marcado (o con lo que se diga). */
export const seleccionVacia = (parte: Partial<Seleccion> = {}): Seleccion => ({ ...Object.fromEntries(COLECCIONES.map((c) => [c, new Set<number>()])), diseno: false, ...parte }) as Seleccion

/** Lo que sale marcado al abrir: todo lo pendiente que se enseña, y el diseño si se puede. */
export const seleccionInicial = (p: Pendiente, o: { diseno?: boolean; version?: null | number } = {}): Seleccion =>
  seleccionVacia({
    ...Object.fromEntries(COLECCIONES.map((c) => [c, new Set(elementosDe(p, c).map((e) => e.id))])),
    diseno: (o.diseno ?? true) && disenoDisponible(p.diseno, o.version),
  })

export const alternar = (s: Seleccion, coleccion: ColeccionPublicable, id: number): Seleccion => {
  const siguiente = new Set(s[coleccion])
  if (siguiente.has(id)) siguiente.delete(id)
  else siguiente.add(id)
  return { ...s, [coleccion]: siguiente }
}

export type EstadoDeGrupo = 'algunos' | 'ninguno' | 'todos'

export const estadoDelGrupo = (s: Seleccion, coleccion: ColeccionPublicable, ids: number[]): EstadoDeGrupo => {
  const marcados = ids.filter((id) => s[coleccion].has(id)).length
  return marcados === 0 ? 'ninguno' : marcados === ids.length ? 'todos' : 'algunos'
}

/** La casilla del grupo: con todos marcados, desmarca; con alguno o ninguno, marca todos. */
export const alternarGrupo = (s: Seleccion, coleccion: ColeccionPublicable, ids: number[]): Seleccion =>
  ({ ...s, [coleccion]: estadoDelGrupo(s, coleccion, ids) === 'todos' ? new Set<number>() : new Set(ids) }) as Seleccion

/** Cuántas cosas se van a publicar: los documentos marcados y el diseño. Lo que dice el botón. */
export const cuantos = (s: Seleccion): number => COLECCIONES.reduce((n, c) => n + s[c].size, 0) + (s.diseno ? 1 : 0)

/** Lo que se avisa de una página antes de publicarla: se puede publicar igual, pero se ve antes. */
export const avisosDePagina = (p: PaginaPendiente): string[] => [
  ...(p.cambios ? ['cambios sin publicar'] : []),
  ...(p.porRellenar ? ['por rellenar'] : []),
  ...(p.seccionesVacias ?? []).map((s) => `«${s}» vacía`),
]

export const avisosDeProducto = (p: ProductoPendiente): string[] => [...(p.cambios ? ['cambios sin publicar'] : []), ...(p.sinPrecio ? ['sin precio'] : []), ...(p.sinImagen ? ['sin foto'] : [])]

/** De un servicio: «sin precio» solo si dice tener uno (fijo o «desde») y no lo tiene, que es lo que no se publica. */
export const avisosDeServicio = (s: ServicioPendiente): string[] => [...(s.cambios ? ['cambios sin publicar'] : []), ...(s.sinPrecio ? ['sin precio'] : []), ...(s.sinImagen ? ['sin foto'] : [])]

export const avisosDe = (coleccion: ColeccionPublicable, e: Elemento & { cambios?: boolean }): string[] =>
  coleccion === 'paginas'
    ? avisosDePagina(e as PaginaPendiente)
    : coleccion === 'productos'
      ? avisosDeProducto(e as ProductoPendiente)
      : coleccion === 'servicios'
        ? avisosDeServicio(e as ServicioPendiente)
        : // Las entradas, los contenidos de los tipos y la estructura: si lo que se publica son cambios sobre algo ya publicado.
          e.cambios
          ? ['cambios sin publicar']
          : []

export const tituloDe = (coleccion: ColeccionPublicable, e: Elemento): string =>
  String(
    (coleccion === 'productos' || coleccion === 'servicios' || esDeEstructura(coleccion) ? (e as DefinicionPendiente | ProductoPendiente).nombre : (e as PaginaPendiente).titulo) ||
      `${NOMBRES[coleccion].singular} ${e.id}`,
  )

/** Lo que acompaña al título en la lista: de un contenido, el nombre de su tipo («Propiedad»), que es lo que lo distingue. */
export const detalleDe = (coleccion: ColeccionPublicable, e: Elemento): null | string => (coleccion === 'contenidos' ? (e as ContenidoPendiente).tipo || null : null)

/** La portada (la página «inicio» sin madre) entre lo pendiente, si está. */
export const portadaDe = (p: Pendiente): null | PaginaPendiente => (p.paginas?.borradores ?? []).find((b) => b.portada) ?? null

/**
 * Si la web va a salir a internet sin portada: es la primera publicación del diseño, la
 * portada no está publicada y no se ha marcado. Se avisa (se puede publicar igual, y el
 * núcleo no lo impide: quien lo decide lo está viendo).
 */
export const saleSinPortada = (p: Pendiente, s: Seleccion): boolean => {
  if (!s.diseno || p.enInternet || !p.paginas || p.paginas.inicioPublicado) return false
  const portada = portadaDe(p)
  return !portada || !s.paginas.has(portada.id)
}

/** Cuántos documentos lleva un pedido, en todas sus listas (la estructura y los contenidos de los tipos también). */
export const documentosDelPedido = (pedido: PedidoDePublicacion): number => COLECCIONES.reduce((n, c) => n + (pedido[EN_EL_PEDIDO[c]] as number[]).length, 0)

/** Un pedido sin nada: todas las listas vacías. */
export const pedidoVacio = (): PedidoDePublicacion => ({ paginas: [], productos: [], servicios: [], entradas: [], contenidos: [], tiposDeContenido: [], taxonomias: [], gruposDeCampos: [], diseno: false })

/**
 * Lo que se manda, en tandas de `TOPE_POR_TANDA`: la estructura primero, después las páginas,
 * los productos, los servicios, las entradas y los contenidos de los tipos, y **el diseño en la
 * última**. Con `versionPublicada` nula el enrutador da un 503, así que el diseño no sale antes
 * que el contenido.
 */
export const tandas = (p: Pendiente, s: Seleccion, o: { tope?: number; version?: null | number } = {}): PedidoDePublicacion[] => {
  const tope = o.tope ?? TOPE_POR_TANDA
  // Se manda en el orden en que se enseña, y solo lo que se enseña.
  const cola = COLECCIONES.flatMap((c) => elementosDe(p, c).filter((e) => s[c].has(e.id)).map((e) => ({ c, id: e.id })))
  const salida: PedidoDePublicacion[] = []
  for (let i = 0; i < cola.length; i += tope) {
    const trozo = cola.slice(i, i + tope)
    const pedido = pedidoVacio()
    for (const x of trozo) pedido[EN_EL_PEDIDO[x.c]].push(x.id)
    salida.push(pedido)
  }
  if (s.diseno) {
    const version = o.version ?? p.diseno.version?.numero
    const diseno = { diseno: true, ...(version ? { version } : {}), esperada: p.diseno.publicada }
    if (salida.length === 0) salida.push({ ...pedidoVacio(), ...diseno })
    else Object.assign(salida[salida.length - 1]!, diseno)
  }
  return salida
}

const vacio = (): ResultadoDeLote => ({ hechos: [], yaEstaban: [], fallidos: [] })

/** Junta los resultados de varias tandas en uno. */
export const juntar = (resultados: PublicacionDeLaWeb[]): PublicacionDeLaWeb => {
  const total = { ...Object.fromEntries(COLECCIONES.map((c) => [c, vacio()])), diseno: null } as PublicacionDeLaWeb
  for (const r of resultados) {
    for (const c of COLECCIONES) {
      // Un núcleo de antes (sin servicios, sin la estructura) no los devuelve: no hay nada que juntar.
      const de = r[c] ?? vacio()
      total[c].hechos.push(...de.hechos)
      total[c].yaEstaban.push(...de.yaEstaban)
      total[c].fallidos.push(...de.fallidos)
    }
    if (r.diseno) total.diseno = r.diseno
  }
  return total
}

/** La portada se pidió y no se ha publicado: en la primera publicación, el diseño no debe salir detrás. */
export const portadaFallida = (p: Pendiente, hasta: PublicacionDeLaWeb[]): boolean => {
  const portada = portadaDe(p)
  return !p.enInternet && portada !== null && hasta.some((r) => r.paginas.fallidos.some((f) => f.id === portada.id))
}

export const MOTIVO_SIN_PORTADA =
  'No se ha publicado el diseño: tu portada no se ha podido publicar, y sin ella tu web abriría en «Esta página no existe». Arréglala y vuelve a publicar.'

const contar = (n: number, singular: string, plural: string) => `${n} ${n === 1 ? singular : plural}`

const enLista = (partes: string[]): string => (partes.length <= 1 ? (partes[0] ?? '') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1]}`)

/** «12 páginas, 3 productos y el diseño»: lo que se va a publicar, en llano. Si el diseño se solicita, no entra aquí. */
export const queSePublica = (s: Seleccion, puede: DisenoPendiente['puede']): string =>
  enLista([...COLECCIONES.filter((c) => s[c].size > 0).map((c) => contar(s[c].size, NOMBRES[c].singular, NOMBRES[c].plural)), ...(s.diseno && puede !== 'solicitar' ? ['el diseño'] : [])])

/**
 * La confirmación antes de publicar: qué se publica y dónde se va a ver. Publicar es un acto
 * humano (§27), y no a ciegas.
 */
export const textoDeConfirmacion = (p: Pendiente, s: Seleccion): string => {
  const que = queSePublica(s, p.diseno.puede)
  const solicita = s.diseno && p.diseno.puede === 'solicitar'
  // «… 6 páginas y 1 producto, y a solicitar …»: con una «y» ya dentro, la coma evita la segunda seguida.
  const accion = que ? `Vas a publicar ${que}${solicita ? `${que.includes(' y ') ? ',' : ''} y a solicitar la publicación del diseño` : ''}.` : 'Vas a solicitar la publicación del diseño.'
  const donde =
    p.enInternet || (s.diseno && p.diseno.puede === 'publicar')
      ? `Se verá en internet, en ${p.dominio}.`
      : solicita
        ? 'Tu web sale a internet cuando el equipo apruebe el diseño.'
        : 'Tu web no sale a internet hasta que se publique el diseño: mientras tanto se ve en la previa.'
  return `${accion} ${donde}`
}

export type Resumen = { publicados: number; yaEstaban: number; fallidos: number; texto: string; tono: 'aviso' | 'ok' }

/** Lo que se dice al terminar: cuánto se publicó, y cuánto no. */
export const resumen = (r: PublicacionDeLaWeb): Resumen => {
  const publicados = COLECCIONES.reduce((n, c) => n + (r[c]?.hechos.length ?? 0), 0)
  const yaEstaban = COLECCIONES.reduce((n, c) => n + (r[c]?.yaEstaban.length ?? 0), 0)
  const fallidos = COLECCIONES.reduce((n, c) => n + (r[c]?.fallidos.length ?? 0), 0) + (r.diseno && r.diseno.accion === 'ninguna' ? 1 : 0)
  const partes: string[] = []
  if (publicados > 0) partes.push(`Publicados: ${publicados}`)
  if (r.diseno?.accion === 'publicado') partes.push(`diseño publicado (v${r.diseno.version})`)
  if (r.diseno?.accion === 'solicitado') partes.push(`publicación del diseño solicitada (v${r.diseno.version})`)
  if (yaEstaban > 0) partes.push(`ya estaban publicados: ${yaEstaban}`)
  if (fallidos > 0) partes.push(fallidos === 1 ? '1 no se pudo' : `${fallidos} no se pudieron`)
  const texto = partes.length === 0 ? 'No se ha publicado nada.' : `${partes.join(' · ')}.`
  return { publicados, yaEstaban, fallidos, texto: texto.charAt(0).toUpperCase() + texto.slice(1), tono: fallidos > 0 ? 'aviso' : 'ok' }
}

/**
 * Dónde se arregla lo que no se ha podido publicar: su editor. Un contenido se edita dentro de
 * su tipo, y el núcleo da el nombre del tipo, no su id: `…/contenido/<id>` lo averigua y lleva
 * allí (`estructura/Contenidos.tsx`).
 */
export const enlaceDe = (espacio: number, sitio: number, coleccion: ColeccionPublicable, id: number, tipoId?: null | number): string =>
  coleccion !== 'contenidos'
    ? `/e/${espacio}/s/${sitio}/${coleccion}/${id}`
    : // Con el tipo, directo a su editor; sin él (lo que devuelve un núcleo de antes), `…/contenido/<id>` lo busca.
      tipoId
      ? `/e/${espacio}/s/${sitio}/contenidos/${tipoId}/${id}`
      : `/e/${espacio}/s/${sitio}/contenido/${id}`

/** El número del botón: hasta 99, y después «99+». */
export const numeroDelBoton = (total: number): null | string => (total <= 0 ? null : total > 99 ? '99+' : String(total))
